'use client';

import { useState, useEffect } from 'react';
import { useAuthState } from '../lib/firebase/app';
import { getUserByEmail } from '../lib/firebase/users';
import { getVenueById } from '../lib/firebase/venues';

export interface UserVenueInfo {
    id: string;
    name: string;
}

interface UseUserVenuesReturn {
    venues: UserVenueInfo[];
    loading: boolean;
    singleVenue: UserVenueInfo | null;
}

export function useUserVenues(): UseUserVenuesReturn {
    const { user, loading: authLoading } = useAuthState();
    const [venues, setVenues] = useState<UserVenueInfo[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchUserVenues = async () => {
            if (authLoading) return;

            if (!user) {
                setVenues([]);
                setLoading(false);
                return;
            }

            try {
                const userData = await getUserByEmail(user.email!);
                if (userData?.AdminOfVenueIds?.length) {
                    const venuePromises = userData.AdminOfVenueIds.map(async (venueId: string) => {
                        const venue = await getVenueById(venueId);
                        return venue ? { id: venueId, name: venue.Name } : null;
                    });

                    const venueResults = await Promise.all(venuePromises);
                    const validVenues = venueResults.filter((v): v is UserVenueInfo => v !== null);
                    setVenues(validVenues);
                } else {
                    setVenues([]);
                }
            } catch (error) {
                console.error('Error fetching user venues:', error);
                setVenues([]);
            } finally {
                setLoading(false);
            }
        };

        fetchUserVenues();
    }, [user, authLoading]);

    const singleVenue = venues.length === 1 ? venues[0] : null;

    return {
        venues,
        loading: loading || authLoading,
        singleVenue,
    };
} 